'use client';

import { useState, useEffect, useMemo, useRef } from 'react';
import { Box, Button, Typography } from '@mui/material';
import DataTable from '../../../components/DataTable';
import ActionMenu from '../../../components/ActionMenu';
import AxleBuilderAddEdit from './AxleBuilderAddEdit';
import AxleBuilderDetail from './AxleBuilderDetail';
import { useLanguage } from '../../../utils/i18n';
import { useNotifier } from '../../../components/Notifier';
import DeleteConfirmDialog from '../../../components/DeleteConfirmDialog';

import { useAxleTypeList, deleteAxleType } from '../../../stores/axleTypeStore';
import { fleetApi } from '../../../api/axiosClient';

const getTzParam = () => `?tz=${Math.abs(new Date().getTimezoneOffset() / 60)}`;

function AxleBuilderPageContent() {
  const currentUserRole = typeof window !== 'undefined' ? localStorage.getItem('rolename') || localStorage.getItem('role_name') || '' : '';
  const { t } = useLanguage();
  const notify = useNotifier();

  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });
  const [searchField, setSearchField] = useState('name');
  const [searchRaw, setSearchRaw] = useState('');
  const [searchValue, setSearchValue] = useState('');

  // LOCAL STATE FOR 'ALL' SEARCH
  const [localData, setLocalData] = useState([]);
  const [localLoading, setLocalLoading] = useState(false);
  const [localTotal, setLocalTotal] = useState(0);

  const [addEditDialog, setAddEditDialog] = useState({ open: false, id: null });
  const [deleteDialog, setDeleteDialog] = useState({ open: false, id: null });
  const [detailDialog, setDetailDialog] = useState({ open: false, data: null });
  const [confirmLoading, setConfirmLoading] = useState(false);

  const addEditRef = useRef();

  const handleOpenDetail = (row) => setDetailDialog({ open: true, data: row });
  const handleCloseDetail = () => setDetailDialog({ open: false, data: null });

  const getAxlesFromRow = (row) => {
    let parsedAxles = [];
    let configObj = row?.axle_configuration;
    if (typeof configObj === 'string') {
      try {
        configObj = JSON.parse(configObj);
      } catch {}
    }
    if (configObj && Array.isArray(configObj.info)) {
      parsedAxles = configObj.info;
    } else if (Array.isArray(row?.axles)) {
      parsedAxles = row.axles;
    }
    return parsedAxles;
  };

  const searchFieldOptions = useMemo(
    () => [
      { value: 'all', label: t('general.searchAll', 'All') },
      { value: 'name', label: t('general.name', 'Name') },
      { value: 'description', label: t('general.name', 'Description') }
    ],
    [t]
  );

  // DEBOUNCE SEARCH INPUT
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchValue(String(searchRaw ?? '').trim());
      setPaginationModel((p) => ({ ...p, page: 0 }));
    }, 700);

    return () => clearTimeout(timer);
  }, [searchRaw, searchField]);

  // DATA FETCHING (HOOK VS LOCAL)
  const hookSearchField = searchField === 'all' ? '' : searchField;
  const hookSearchValue = searchField === 'all' ? '' : searchValue;

  const {
    data: dataFromHook,
    isLoading: hookLoading,
    totalRecord: hookTotal
  } = useAxleTypeList(paginationModel.page, paginationModel.pageSize, hookSearchField, hookSearchValue);

  const rows = searchField === 'all' ? localData : dataFromHook || [];
  const isLoading = searchField === 'all' ? localLoading : hookLoading;
  const totalRecords = searchField === 'all' ? localTotal : hookTotal || 0;

  // EFFECT FOR 'ALL' SEARCH
  useEffect(() => {
    if (searchField !== 'all') return;
    const controller = new AbortController();

    const fetchAllSearch = async () => {
      setLocalLoading(true);
      try {
        const start = paginationModel.page * paginationModel.pageSize;
        const length = paginationModel.pageSize;

        let filters = [];
        if (searchValue) {
          filters = [
            { name: 'name', value: searchValue },
            { name: 'description', value: searchValue }
          ];
        }

        const payload = {
          paging: { start, length },
          orders: { columns: ['name'], ascending: true },
          columns: [{ logic_operator: 'like', name: 'name', operator: 'AND', value: '' }],
          filters: filters
        };

        const resp = await fleetApi.post('/axle-type/search' + getTzParam(), payload, { signal: controller.signal });
        const data = resp?.data ?? null;
        const fetchedRows = Array.isArray(data) ? data : (data?.data ?? []);
        const total = Array.isArray(data) ? fetchedRows.length : (data?.totalRecord ?? data?.totalRecords ?? data?.dataSize ?? 0);

        setLocalData(fetchedRows);
        setLocalTotal(total);
      } catch (err) {
        if (err.name !== 'AbortError') console.error('axle type search all error', err);
      } finally {
        if (!controller.signal.aborted) setLocalLoading(false);
      }
    };

    fetchAllSearch();
    return () => controller.abort();
  }, [searchField, searchValue, paginationModel]);

  const handleFetchAllForExport = async () => {
    try {
      const length = totalRecords > 0 ? totalRecords : 9999;

      let filters = [];
      let columns = [];

      if (searchField === 'all' && searchValue) {
        if (columns.length === 0) columns.push({ logic_operator: 'like', name: 'name', operator: 'AND', value: '' });
        filters = [
          { name: 'name', value: searchValue },
          { name: 'description', value: searchValue }
        ];
      } else if (searchValue) {
        const opt = searchFieldOptions.find((o) => o.value === searchField);
        if (opt) {
          columns.push({
            name: opt.fieldName || searchField,
            logic_operator: 'like',
            operator: 'AND',
            value: searchValue
          });
        }
      }

      if (columns.length === 0) {
        columns.push({ logic_operator: 'like', name: 'name', operator: 'AND', value: '' });
      }

      const payload = {
        paging: { start: 0, length },
        orders: { columns: ['name'], ascending: true },
        columns: columns,
        filters: filters
      };

      const resp = await fleetApi.post('/axle-type/search' + getTzParam(), payload);
      const data = resp?.data ?? null;
      return Array.isArray(data) ? data : (data?.data ?? []);
    } catch (err) {
      console.error('axle type export all error', err);
      return [];
    }
  };

  const handlePageChange = (model) => {
    setPaginationModel((prev) => {
      if (prev.page === model.page && prev.pageSize === model.pageSize) {
        return prev;
      }
      return model;
    });
  };

  const handleOpenAdd = () => setAddEditDialog({ open: true, id: null });
  const handleOpenEdit = (id) => setAddEditDialog({ open: true, id });
  const handleCloseAddEdit = () => setAddEditDialog({ open: false, id: null });

  const handleOpenDelete = (id) => setDeleteDialog({ open: true, id });
  const handleCloseDelete = () => {
    if (confirmLoading) return;
    setDeleteDialog({ open: false, id: null });
  };

  const handleConfirmDelete = async () => {
    if (deleteDialog.id != null) {
      setConfirmLoading(true);
      try {
        await deleteAxleType(deleteDialog.id);
        notify({ message: t('notify.deleted', 'Deleted successfully'), severity: 'success' });
        setPaginationModel((p) => ({ ...p }));
      } catch (err) {
        console.error(err);
        notify({ message: t('notify.deleteFailed', 'Failed to delete'), severity: 'error' });
      } finally {
        setConfirmLoading(false);
        handleCloseDelete();
      }
    }
  };

  const handleSuccessAddEdit = (message) => {
    notify({ message, severity: 'success' });
    setPaginationModel((p) => ({ ...p }));
    handleCloseAddEdit();
  };

  const columns = useMemo(
    () => [
      { field: 'name', headerName: t('axleBuilder.name', 'Name'), flex: 1 },

      { field: 'description', headerName: t('general.description', 'Description'), flex: 2 },
      {
        field: 'axle_detail',
        headerAlign: 'center',
        headerName: t('axleBuilder.axleDetail', 'Axle Detail'),
        width: 150,
        valueGetter: (paramsOrValue, rowOrNull) => {
          const row = rowOrNull || paramsOrValue?.row;
          const axles = getAxlesFromRow(row);
          if (!axles || axles.length === 0) return '-';

          return axles.map((axle, index) => {
            const types = [];
            if (axle.is_steering) types.push('Steering');
            if (axle.is_driver) types.push('Driver');
            if (axle.is_lift_axle) types.push('Lift Axle');
            if (axle.is_twin_tire) types.push('Twin Tire');
            return `Axle ${index + 1}: ${types.join(', ')}`;
          });
        },
        renderCell: (params) => {
          const axles = getAxlesFromRow(params.row);
          const totalAxle = axles.length;
          let totalTire = 0;
          axles.forEach((axle) => {
            let axleTires = 2; // base tires
            if (axle.is_twin_tire && !axle.is_steering) axleTires = 4;
            totalTire += axleTires;
          });

          return (
            <Box display="flex" flexDirection="column" justifyContent="center" height="100%">
              <Button
                variant="outlined"
                size="small"
                sx={{
                  textTransform: 'none',
                  borderRadius: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between'
                }}
                onClick={() => handleOpenDetail(params.row)}
              >
                <Typography fontWeight="bold">{totalAxle} Axles</Typography>
                <Typography color="text.secondary">{totalTire} Tires</Typography>
              </Button>
            </Box>
          );
        }
      },
      ...(currentUserRole === 'Administrator'
        ? [
            { field: 'created_by', headerName: t('general.createdBy', 'Created By'), flex: 0.5, hide: true },
            { field: 'created_date', headerName: t('general.createdDate', 'Created Date'), flex: 0.5, hide: true },
            { field: 'modified_by', headerName: t('general.modifiedBy', 'Modified By'), flex: 0.5, hide: true },
            { field: 'modified_date', headerName: t('general.modifiedDate', 'Modified Date'), flex: 0.5, hide: true }
          ]
        : []),
      {
        field: 'actions',
        headerName: t('general.actions', 'Actions'),
        sortable: false,
        width: 100,
        headerAlign: 'center',
        align: 'center',
        renderCell: (params) => <ActionMenu row={params.row} onEdit={handleOpenEdit} onDelete={handleOpenDelete} mode="inline" />
      }
    ],
    [currentUserRole, t]
  );

  return (
    <div>
      <DataTable
        title={t('axleBuilder.master', 'Master Axle Builder')}
        onFetchAllForExport={handleFetchAllForExport}
        rows={rows}
        columns={columns}
        rowCount={totalRecords}
        loading={isLoading}
        pageSizeOptions={[10, 25, 50]}
        paginationMode="server"
        paginationModel={paginationModel}
        onPaginationModelChange={handlePageChange}
        searchQuery={searchRaw}
        onSearch={({ field, value }) => {
          setSearchField(field);
          setSearchRaw(value ?? '');
          setPaginationModel((p) => ({ ...p, page: 0 }));
        }}
        searchFieldOptions={searchFieldOptions}
        selectedSearchField={searchField}
        onSearchFieldChange={(f) => {
          setSearchField(f);
          setSearchRaw('');
          setSearchValue('');
          setPaginationModel((p) => ({ ...p, page: 0 }));
        }}
        onAddClick={handleOpenAdd}
        searchLabel={t('general.searchByName', 'Search by Name')}
        rowHeight={90}
      />

      <AxleBuilderAddEdit
        open={addEditDialog.open}
        id={addEditDialog.id}
        onClose={handleCloseAddEdit}
        onSuccess={handleSuccessAddEdit}
        onError={(msg) => notify({ message: msg, severity: 'error' })}
        ref={addEditRef}
      />

      <AxleBuilderDetail open={detailDialog.open} onClose={handleCloseDetail} data={detailDialog.data} />

      <DeleteConfirmDialog
        open={deleteDialog.open}
        loading={confirmLoading}
        onClose={handleCloseDelete}
        onConfirm={handleConfirmDelete}
        title={t('deleteConfirm.title', 'Confirm Delete')}
        message={t('deleteConfirm.message', 'Are you sure you want to delete this item?')}
        confirmLabel={t('deleteConfirm.label', 'Delete')}
        cancelLabel={t('deleteConfirm.cancel', 'Cancel')}
      />
    </div>
  );
}

export default function AxleBuilderPage() {
  return <AxleBuilderPageContent />;
}
